import React from 'react';
import { Navbar } from './Navbar';
import {
  LayoutDashboard,
  ClipboardCheck,
  ShieldCheck,
  Settings as SettingsIcon,
} from 'lucide-react';
import type { NavigationTab } from '../../types';

export interface LayoutProps {
  children: React.ReactNode;
  activeTab: NavigationTab;
  onSelectTab: (tab: NavigationTab) => void;
  selectedMachineName?: string;
  onManualSync?: () => void;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  activeTab,
  onSelectTab,
  selectedMachineName,
  onManualSync,
}) => {
  const navTabs: Array<{ id: NavigationTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'inspection', label: 'Inspection', icon: ClipboardCheck },
    { id: 'admin', label: 'Admin', icon: ShieldCheck },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col selection:bg-blue-600 selection:text-white">
      {/* Top Header Navbar */}
      <Navbar
        activeTab={activeTab}
        onSelectTab={onSelectTab}
        selectedMachineName={selectedMachineName}
        onManualSync={onManualSync}
      />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 pb-28 md:pb-10">
        {children}
      </main>

      {/* Mobile Ergonomic Bottom Tab Bar (WCAG 2.5.5 >= 48px touch targets) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-xl safe-bottom">
        <div className="grid grid-cols-4 h-16 max-w-md mx-auto">
          {navTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onSelectTab(tab.id)}
                className={`flex flex-col items-center justify-center gap-1 min-h-[48px] py-1 transition-colors select-none active:scale-95 ${
                  isActive
                    ? 'text-blue-600 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <div
                  className={`p-1 rounded-full transition-all ${
                    isActive ? 'bg-blue-50 ring-1 ring-blue-200' : ''
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-[11px] leading-none tracking-tight">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
